// Authentication composable for managing user state
import {
  ref, readonly, computed, onMounted,
} from 'vue';
import configService from '@/services/remoteConfig';
import { clearCollection } from '@/composables/useCollection';

// Create shared singleton state
const isAuthenticated = ref(false);
const isLoading = ref(true);
const error = ref(null);

// Computed property to check auth status
const hasCompletedAuth = computed(() => isAuthenticated.value);

// Check if we have valid Discogs tokens in localStorage
const checkLocalTokens = () => {
  try {
    const authData = JSON.parse(localStorage.getItem('discogs_auth'));

    if (!authData || !authData.accessToken || !authData.accessTokenSecret) {
      return false;
    }

    // Check if tokens are expired
    const now = Date.now();
    const tokenTime = authData.timestamp || 0;
    const TOKEN_EXPIRY = configService.get('tokenExpiryTime'); // Get from Remote Config

    return (now - tokenTime) < TOKEN_EXPIRY;
  } catch (err) {
    console.error('Error checking Discogs tokens:', err);
    return false;
  }
};

// Initialize auth state from localStorage
const initAuthState = () => {
  isLoading.value = true;

  try {
    const hasTokens = checkLocalTokens();
    isAuthenticated.value = hasTokens;
  } catch (err) {
    error.value = err.message;
    console.error('Auth state error:', err);
  } finally {
    isLoading.value = false;
  }
};

// Clear stored tokens
const clearTokens = () => {
  localStorage.removeItem('discogs_auth');
  clearCollection();
  isAuthenticated.value = false;
};

// Get the stored Discogs tokens
const getDiscogsTokens = () => {
  try {
    return JSON.parse(localStorage.getItem('discogs_auth')) || null;
  } catch (err) {
    console.error('Error getting tokens:', err);
    return null;
  }
};

// Sign out the current user
const logout = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    clearTokens();
  } catch (err) {
    error.value = err.message;
    console.error('Logout error:', err);
  } finally {
    isLoading.value = false;
  }
};

// Start Discogs Authentication Flow
const loginWithDiscogs = async callbackUrl => {
  isLoading.value = true;
  error.value = null;

  try {
    // We need to use the auth service to get the URL
    const { initiateAuth } = await import('@/services/auth');

    const authUrl = await initiateAuth(callbackUrl);

    if (!authUrl) {
      throw new Error('Failed to get authorization URL from Discogs');
    }

    // Redirect to Discogs OAuth page
    window.location.href = authUrl;
    return true;
  } catch (err) {
    error.value = err.message;
    console.error('Discogs login error:', err);
    isLoading.value = false;
    return false;
  }
};

// Watch for changes to localStorage
const setupStorageListener = () => {
  window.addEventListener('storage', event => {
    if (event.key === 'discogs_auth') {
      initAuthState();
    }
  });
};

// Export the composable
export default function useAuth() {
  // Initialize on first use
  onMounted(() => {
    initAuthState();
    setupStorageListener();
  });

  return {
    // State (read-only)
    isAuthenticated: readonly(isAuthenticated),
    hasCompletedAuth: readonly(hasCompletedAuth),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Actions
    logout,
    loginWithDiscogs,
    getDiscogsTokens,
  };
}
